import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Spinner, Alert, Row, Col, Nav, Modal } from 'react-bootstrap';
import {
    FaEye,
    FaDownload,
    FaUserTie,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaFileContract,
    FaExclamationTriangle,
    FaCheckCircle,
    FaClock,
    FaFilter,
    FaTimesCircle,
    FaUsers,
    FaFilePdf,
    FaSignature,
    FaTimes, FaSpinner
} from 'react-icons/fa';
import HeaderTopSection from "../../components/partials/HeaderTop";
import ContractModal from '../../components/contract/ContractModalClient';
import PDFViewerModal from '../../components/PDFViewerModal';
import ContractService from '../../services/ContractService';
import '../../assets/css/ContractsPage.css';
import Banner from "../../components/partials/Banner";
import ClientSignatureModal from '../../components/contract/client/ClientSignatureModal';
import AdminSignatureModal from '../../components/contract/client/AdminSignatureModal';
import AuthService from '../../services/AuthService';

const ClientContractsPage = () => {
    const [contracts, setContracts] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [activeFilter, setActiveFilter] = useState('PENDING_SIGNATURE_CLIENT_FALSE');
    const [selectedContract, setSelectedContract] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [signingId, setSigningId] = useState(null);
    const [generatingId, setGeneratingId] = useState(null);

    // --- États liés à la modal PDF (désormais gérée par PDFViewerModal) ---
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfViewUrl, setPdfViewUrl] = useState(null);
    const [currentPdfTitle, setCurrentPdfTitle] = useState('');
    const [pdfContractRef, setPdfContractRef] = useState(null);

    const [showClientSignatureModal, setShowClientSignatureModal] = useState(false);
    const [contractToSign, setContractToSign] = useState(null);
    const [showAdminSignatureModal, setShowAdminSignatureModal] = useState(false);
    const [contractToAdminSign, setContractToAdminSign] = useState(null);
    const [currentClientId, setCurrentClientId] = useState(null);
    const [currentUserRole, setCurrentUserRole] = useState(null);

    useEffect(() => {
        const user = AuthService.getCurrentUser();
        if (user) {
            setCurrentClientId(user.id);
            setCurrentUserRole(user.roles && user.roles.length > 0 ? user.roles[0] : null);
        }
    }, []);

    useEffect(() => {
        if (currentClientId) {
            fetchContracts();
        }
    }, [currentClientId]);

    useEffect(() => {
        filterContracts();
    }, [contracts, activeFilter]);

    const fetchContracts = async () => {
        if (!currentClientId) {
            setIsLoading(false);
            setError('Utilisateur non connecté');
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const allContracts = await ContractService.getAllContracts();
            const clientContracts = allContracts.filter(contract => {
                return contract.clientId === currentClientId ||
                    contract.client?.id === currentClientId ||
                    contract.client?.user?.id === currentClientId;
            });

            setContracts(clientContracts);

            console.log('Contrats filtrés pour le client:', {
                clientId: currentClientId,
                totalContracts: allContracts.length,
                filteredContracts: clientContracts.length,
                contracts: clientContracts
            });

        } catch (err) {
            console.error('Failed to fetch contracts:', err);
            setError('Échec du chargement des contrats. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    const filterContracts = () => {
        let filtered = [];

        switch (activeFilter) {
            case 'PENDING_SIGNATURE_CLIENT_FALSE':
                filtered = contracts.filter(contract =>
                    contract.contractStatus === 'PENDING_SIGNATURE_CLIENT' &&
                    !contract.clientSigned
                );
                break;

            case 'PENDING_SIGNATURE_CLIENT_TRUE':
                filtered = contracts.filter(contract =>
                    (contract.contractStatus === 'PENDING_SIGNATURE_CLIENT' && contract.clientSigned) ||
                    contract.contractStatus === 'PENDING_SIGNATURE_CLIENT_DHS'
                );
                break;

            case 'ACTIVE':
                filtered = contracts.filter(contract =>
                    contract.contractStatus === 'ACTIVE'
                );
                break;

            case 'COMPLETED':
                filtered = contracts.filter(contract =>
                    contract.contractStatus === 'TERMINATED' ||
                    contract.contractStatus === 'EXPIRED' ||
                    contract.contractStatus === 'CANCELLED'
                );
                break;

            default:
                filtered = contracts;
        }

        setFilteredContracts(filtered);
    };

    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
    };

    const handleViewContract = (contract) => {
        setSelectedContract(contract);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedContract(null);
    };

    /**
     * Ouvre la modal PDF (PDFViewerModal) pour une URL donnée (presignée ou non).
     * Le fetch, la conversion en blob et le rendu sont désormais entièrement
     * gérés à l'intérieur de PDFViewerModal (rendu react-pdf, sans barre native).
     */
    const handleViewPdfFromUrl = (url, title, contractRef = null) => {
        if (!url) {
            alert('Aucun PDF disponible');
            return;
        }
        setPdfViewUrl(url);
        setCurrentPdfTitle(title);
        setPdfContractRef(contractRef);
        setShowPdfModal(true);
    };

    /**
     * Fonction pour générer un nouveau PDF
     */
    const handleGenerateContract = async (contract) => {
        setGeneratingId(contract.id);
        try {
            const pdfBytes = await ContractService.generateContractPdf(contract.id);
            const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(pdfBlob);

            setPdfViewUrl(blobUrl);
            setCurrentPdfTitle(`Contrat généré - ${contract.contractRef}`);
            setPdfContractRef(contract.contractRef);
            setShowPdfModal(true);

            // Rafraîchir la liste des contrats
            fetchContracts();

        } catch (error) {
            console.error('Erreur lors de la génération du contrat:', error);
            alert(`Erreur lors de la génération: ${error.message}`);
        } finally {
            setGeneratingId(null);
        }
    };

    const handleSignContract = (contract) => {
        if (currentClientId && (
            contract.clientId === currentClientId ||
            contract.client?.id === currentClientId ||
            contract.client?.user?.id === currentClientId
        )) {
            setContractToSign(contract);
            setShowClientSignatureModal(true);
        } else {
            alert('Vous ne pouvez signer que vos propres contrats.');
        }
    };

    const handleAdminSignContract = (contract) => {
        setContractToAdminSign(contract);
        setShowAdminSignatureModal(true);
    };

    const closePdfModal = () => {
        setShowPdfModal(false);
        if (pdfViewUrl && pdfViewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(pdfViewUrl);
        }
        setPdfViewUrl(null);
        setCurrentPdfTitle('');
        setPdfContractRef(null);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Non spécifié';
        return new Date(timestamp).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING_SIGNATURE':
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">
                    <FaClock className="me-1" /> En attente génération
                </Badge>;
            case 'PENDING_SIGNATURE_CLIENT':
                return <Badge bg="warning" className="fs-6 py-2 px-3 rounded-pill">
                    <FaSignature className="me-1" /> En attente signature
                </Badge>;
            case 'PENDING_SIGNATURE_CLIENT_DHS':
                return <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill">
                    <FaSignature className="me-1" /> Signé par DHS
                </Badge>;
            case 'ACTIVE':
                return <Badge bg="primary" className="fs-6 py-2 px-3 rounded-pill">
                    <FaCheckCircle className="me-1" /> Actif
                </Badge>;
            case 'EXPIRED':
                return <Badge bg="danger" className="fs-6 py-2 px-3 rounded-pill">
                    <FaTimesCircle className="me-1" /> Expiré
                </Badge>;
            case 'TERMINATED':
                return <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill">
                    <FaCheckCircle className="me-1" /> Terminé
                </Badge>;
            case 'CANCELLED':
                return <Badge bg="dark" className="fs-6 py-2 px-3 rounded-pill">
                    <FaTimesCircle className="me-1" /> Annulé
                </Badge>;
            default:
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">Inconnu</Badge>;
        }
    };

    const getAvatarUrl = (user) => {
        return user?.profilePictureUrl || 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/media-service/dhs.png';
    };

    const getFullName = (user) => {
        if (!user) return 'Information non disponible';
        return `${user.firstName} ${user.lastName}`.trim();
    };

    const getEmail = (user) => {
        return user?.email || 'Email non disponible';
    };

    const getPhone = (user) => {
        return user?.phoneNumber || 'Téléphone non disponible';
    };

    const getLocation = (user) => {
        if (!user) return 'Localisation non disponible';
        return `${user.city || ''}, ${user.country || ''}`.trim().replace(/^,\s*|\s*,$/g, '');
    };

    const getServiceLabel = (service) => {
        if (!service) return 'Service non spécifié';
        const serviceLabels = {
            'garde_enfant': 'Garde d\'enfant',
            'menage': 'Ménage',
            'cuisine': 'Cuisine',
            'jardinage': 'Jardinage'
        };
        return serviceLabels[service] || service.replace('_', ' ');
    };

    const getFilterStats = () => {
        return {
            PENDING_SIGNATURE_CLIENT_FALSE: contracts.filter(c =>
                c.contractStatus === 'PENDING_SIGNATURE_CLIENT' && !c.clientSigned
            ).length,
            PENDING_SIGNATURE_CLIENT_TRUE: contracts.filter(c =>
                (c.contractStatus === 'PENDING_SIGNATURE_CLIENT' && c.clientSigned) ||
                c.contractStatus === 'PENDING_SIGNATURE_CLIENT_DHS'
            ).length,
            ACTIVE: contracts.filter(c => c.contractStatus === 'ACTIVE').length,
            COMPLETED: contracts.filter(c =>
                ['TERMINATED', 'EXPIRED', 'CANCELLED'].includes(c.contractStatus)
            ).length
        };
    };

    const filterStats = getFilterStats();

    const getActionButton = (contract) => {
        switch (activeFilter) {
            case 'PENDING_SIGNATURE_CLIENT_FALSE':
                if (currentClientId && (
                    contract.clientId === currentClientId ||
                    contract.client?.id === currentClientId ||
                    contract.client?.user?.id === currentClientId
                )) {
                    return (
                        <Button
                            variant="success"
                            className="w-100"
                            onClick={() => handleSignContract(contract)}
                            size="lg"
                        >
                            <FaSignature className="me-2" /> Signer le contrat
                        </Button>
                    );
                }
                return null;

            case 'PENDING_SIGNATURE_CLIENT_TRUE':
                return null;

            case 'ACTIVE':
                return (
                    <Button
                        style={{background: 'linear-gradient(135deg, rgb(23, 50, 71) 0%, #198754 100%)'}}
                        className="w-100"
                        onClick={() => handleViewPdfFromUrl(
                            contract.contractClientActivePresigned || contract.contractClientActive,
                            `Contrat Actif - ${contract.contractRef}`,
                            contract.contractRef
                        )}
                        size="lg"
                    >
                        <FaEye className="me-2" /> Afficher le PDF
                    </Button>
                );

            default:
                return (
                    <Button
                        style={{background: 'linear-gradient(135deg, rgb(23, 50, 71) 0%, #198754 100%)'}}
                        className="w-100"
                        onClick={() => handleViewPdfFromUrl(
                            contract.contractClientActivePresigned || contract.contractClientActive,
                            `Contrat Actif - ${contract.contractRef}`,
                            contract.contractRef
                        )}
                        size="lg"
                    >
                        <FaEye className="me-2" /> Afficher le PDF
                    </Button>
                );
        }
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Mes Contrats"
                subtitle={`Consultez et gérez tous vos contrats de service ${currentClientId ? `(Client ID: ${currentClientId})` : ''}`}
            />

            <div className="container-fluid py-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                <div className="container">
                    <div className="row">
                        {/* Sidebar de filtrage */}
                        <div className="col-lg-3 mb-7">
                            <Card className="shadow-sm border-0 sticky-top" style={{ top: '10px', borderRadius: '16px', overflow: 'hidden' }}>
                                <Card.Header className="bg-gradient text-white border-0 py-4" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                }}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <FaFilter className="me-2" size={20} />
                                        <h5 className="mb-0 fw-bold">Filtres des Contrats</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Nav variant="pills" className="flex-column">
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'active-pending' : ''}`}
                                                onClick={() => handleFilterChange('PENDING_SIGNATURE_CLIENT_FALSE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? '4px solid #ffc107' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? '#fff3cd' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaSignature className="me-2" style={{ color: activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? '#ffc107' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'bold' : 'medium'}`}>
                                                        À Signer
                                                    </span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'warning' : 'light'}
                                                    text={activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'dark' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.PENDING_SIGNATURE_CLIENT_FALSE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'active-approved' : ''}`}
                                                onClick={() => handleFilterChange('PENDING_SIGNATURE_CLIENT_TRUE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? '4px solid #28a745' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? '#d4edda' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaSignature className="me-2" style={{ color: activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? '#28a745' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'bold' : 'medium'}`}>
                                                        En Attente DHS
                                                    </span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'success' : 'light'}
                                                    text={activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.PENDING_SIGNATURE_CLIENT_TRUE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'ACTIVE' ? 'active-completed' : ''}`}
                                                onClick={() => handleFilterChange('ACTIVE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'ACTIVE' ? '4px solid #007bff' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'ACTIVE' ? '#cce7ff' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaCheckCircle className="me-2" style={{ color: activeFilter === 'ACTIVE' ? '#007bff' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'ACTIVE' ? 'bold' : 'medium'}`}>Contrats Actifs</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'ACTIVE' ? 'primary' : 'light'}
                                                    text={activeFilter === 'ACTIVE' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.ACTIVE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'COMPLETED' ? 'active-rejected' : ''}`}
                                                onClick={() => handleFilterChange('COMPLETED')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'COMPLETED' ? '4px solid #6c757d' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'COMPLETED' ? '#e9ecef' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaTimesCircle className="me-2" style={{ color: activeFilter === 'COMPLETED' ? '#6c757d' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'COMPLETED' ? 'bold' : 'medium'}`}>Contrats Finalisés</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'COMPLETED' ? 'secondary' : 'light'}
                                                    text={activeFilter === 'COMPLETED' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.COMPLETED}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Contenu principal */}
                        <div className="col-lg-9">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" size="lg" />
                                    <p className="mt-3 fs-5 text-muted">Chargement des contrats...</p>
                                </div>
                            ) : error ? (
                                <Alert variant="danger" className="mx-auto" style={{ maxWidth: '600px' }}>
                                    <div className="text-center">
                                        <FaExclamationTriangle size={32} className="mb-3 text-danger" />
                                        <h5 className="alert-heading">Erreur de chargement</h5>
                                        <p className="mb-3">{error}</p>
                                        <Button variant="primary" onClick={fetchContracts} size="lg">
                                            Réessayer le chargement
                                        </Button>
                                    </div>
                                </Alert>
                            ) : filteredContracts.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="py-5">
                                        <FaFileContract size={64} className="text-muted mb-4" />
                                        <h3 className="text-muted mb-3">
                                            {activeFilter === 'PENDING_SIGNATURE_CLIENT_FALSE' ? 'Aucun contrat à signer' :
                                                activeFilter === 'PENDING_SIGNATURE_CLIENT_TRUE' ? 'Aucun contrat en attente de DHS' :
                                                    activeFilter === 'ACTIVE' ? 'Aucun contrat actif' :
                                                        'Aucun contrat finalisé'}
                                        </h3>
                                        <Button variant="primary" size="lg" onClick={fetchContracts}>
                                            Actualiser la liste
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Row xs={1} md={2} lg={3} className="g-4">
                                    {filteredContracts.map(contract => (
                                        <Col key={contract.id}>
                                            <Card className="h-100 shadow-lg border-0 hover-lift"
                                                  style={{
                                                      borderRadius: '16px',
                                                      transition: 'all 0.3s ease',
                                                      border: contract.contractStatus === 'ACTIVE' ? '2px solid #007bff' :
                                                          contract.contractStatus === 'PENDING_SIGNATURE_CLIENT' ? '2px solid #ffc107' :
                                                              '2px solid #6c757d'
                                                  }}>
                                                <div className="position-relative">
                                                    <Card.Img
                                                        variant="top"
                                                        src={getAvatarUrl(contract.client?.user)}
                                                        style={{
                                                            height: '180px',
                                                            objectFit: 'cover',
                                                            borderTopLeftRadius: '16px',
                                                            borderTopRightRadius: '16px'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/media-service/dhs.png';
                                                        }}
                                                    />
                                                    <div className="position-absolute top-0 end-0 m-3">
                                                        {getStatusBadge(contract.contractStatus)}
                                                    </div>
                                                </div>

                                                <Card.Body className="d-flex flex-column p-4">
                                                    <div className="mb-3">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <div className="bg-primary rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                 style={{width: '40px', height: '40px'}}>
                                                                <FaFileContract size={16} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-0 text-dark fw-bold">Contrat #{contract.contractRef}</h6>
                                                                <small className="text-muted">
                                                                    {getServiceLabel(contract.professional?.selectedService)}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mb-3">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <div className="bg-success rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                 style={{width: '32px', height: '32px'}}>
                                                                <FaUserTie size={12} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <small className="text-dark fw-medium">Client</small>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {getFullName(contract.client?.user)}
                                                                    {contract.clientId === currentClientId ||
                                                                    contract.client?.id === currentClientId ||
                                                                    contract.client?.user?.id === currentClientId ? (
                                                                        <Badge bg="info" className="ms-2">Vous</Badge>
                                                                    ) : null}
                                                                </small>
                                                            </div>
                                                        </div>

                                                        <div className="d-flex align-items-center mb-2">
                                                            <div className="bg-info rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                 style={{width: '32px', height: '32px'}}>
                                                                <FaUserTie size={12} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <small className="text-dark fw-medium">Professionnel</small>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {getFullName(contract.professional?.user)}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mb-3">
                                                        {contract.serviceFee && (
                                                            <Badge bg="success" className="me-2 mb-2">
                                                                {new Intl.NumberFormat('fr-FR').format(contract.serviceFee)} FCFA
                                                            </Badge>
                                                        )}
                                                        {contract.contractDuration && (
                                                            <Badge bg="info" className="mb-2">
                                                                {contract.contractDuration} mois
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    <div className="mt-auto d-flex flex-column gap-2">
                                                        <Button
                                                            style={{background: 'linear-gradient(135deg, rgb(23, 50, 71) 0%, rgb(60, 111, 205) 100%)'}}
                                                            variant="primary"
                                                            className="w-100"
                                                            onClick={() => handleViewContract(contract)}
                                                            size="lg"
                                                        >
                                                            <FaEye className="me-2" /> Voir détails
                                                        </Button>

                                                        {getActionButton(contract)}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal pour afficher les détails du contrat */}
            <ContractModal
                contract={selectedContract}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />

            {/* Modal PDF - remplacée par le composant PDFViewerModal (rendu react-pdf, sans barre native) */}
            <PDFViewerModal
                show={showPdfModal}
                onHide={closePdfModal}
                pdfUrl={pdfViewUrl}
                title={currentPdfTitle}
                contractId={pdfContractRef}
            />

            {/* Modal pour la signature client */}
            <ClientSignatureModal
                contract={contractToSign}
                isOpen={showClientSignatureModal}
                onClose={() => {
                    setShowClientSignatureModal(false);
                    setContractToSign(null);
                }}
                onSuccess={() => {
                    setShowClientSignatureModal(false);
                    setContractToSign(null);
                    fetchContracts();
                }}
            />

            {/* Modal pour la signature admin */}
            <AdminSignatureModal
                contract={contractToAdminSign}
                isOpen={showAdminSignatureModal}
                onClose={() => {
                    setShowAdminSignatureModal(false);
                    setContractToAdminSign(null);
                }}
                onSuccess={() => {
                    setShowAdminSignatureModal(false);
                    setContractToAdminSign(null);
                    fetchContracts();
                }}
            />
        </>
    );
};

export default ClientContractsPage;